import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormInstance } from 'antd/lib/form';
import { Form, Input } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_FALLBACK_SITE_KEY } from '../../../config';
import { Link } from 'react-router-dom';
import { IWithGoogleReCaptchaProps, withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { ISignUpCredentialsByEmail, RecaptchaActions, SignUpTier } from './types';
import { RECAPTCHA_ERROR, validateMessages } from './constants';
import { withSignUp } from './withSignUp';
import { SocialLoginButtons } from '../login/socialLoginButtons';
import { CognitoAction } from '../../../services/cognito';
import { SignUpDivider } from './SignUpDivider';
import { PrimaryButton } from '../../common/primaryButton';
import { ISignUpByEmailRequest, signUpByEmail } from '../../../slices/signup/signup.thunks';
import { RootState } from '../../../slices/rootReducer';
import { selectSignUpByEmail } from '../../../slices/signup/signup.selectors';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { userLogout } from '../../../slices/auth/login.thunks';
import { getQueryParam } from '../../../utils/queryParameters';
import { showErrorMessage } from '../../../slices/notifications';

import styles from './SignUpByEmail.module.scss';

interface IDispatchProps {
  signUpByEmail: (values: ISignUpByEmailRequest) => void;
  userLogout: () => void;
  showErrorMessage: (message: string) => void;
}

export interface IUtmParams {
  utmTerm?: string;
  utmCampaign?: string;
  utmSource?: string;
  utmMedium?: string;
}
interface IMapStateToPros {
  loading: boolean;
  showReCaptchaV2: boolean;
  utmParams: IUtmParams;
}

interface IComponentProps {
  tier: SignUpTier;
}

type Props = IComponentProps & IMapStateToPros & IDispatchProps & Partial<IWithGoogleReCaptchaProps>;

class SignUpByEmailComponent extends Component<Props> {
  private readonly formRef: React.RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);

    this.formRef = React.createRef<FormInstance>();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRecaptchaV2Change = this.handleRecaptchaV2Change.bind(this);
  }

  componentDidMount(): void {
    const { userLogout } = this.props;
    userLogout();
  }

  private async handleSubmit(values: ISignUpCredentialsByEmail) {
    const { email } = values;
    const { tier, utmParams, showErrorMessage, signUpByEmail } = this.props;
    const recaptchaToken = await this.executeRecaptcha(RecaptchaActions.SignUpByEmail);
    if (!recaptchaToken) {
      showErrorMessage(RECAPTCHA_ERROR);
      return;
    }
    signUpByEmail({
      email,
      recaptchaToken,
      useFallback: false,
      tier,
      utmParams
    });
  }

  private handleRecaptchaV2Change(recaptchaToken: string | null) {
    const email = this.formRef.current?.getFieldValue('email');
    if (recaptchaToken === null || !email) {
      return;
    }
    const { tier, utmParams } = this.props;
    this.props.signUpByEmail({
      email,
      recaptchaToken,
      useFallback: true,
      tier,
      utmParams
    });
  }

  private async executeRecaptcha(action: string): Promise<string | null> {
    const { googleReCaptchaProps: { executeRecaptcha } = {} } = this.props;

    if (!executeRecaptcha) {
      return null;
    }

    return executeRecaptcha(action);
  }

  private renderTitle() {
    if (AccessTypes.Lite === this.props.tier) {
      return (
        <div className={styles.title}>Create your free account</div>
      );
    }
    return (
      <div className={styles.title}>Create your account</div>
    );
  }

  private renderSocialButtons() {
    const action = AccessTypes.Professional === this.props.tier
      ? CognitoAction.SignUpPro
      : CognitoAction.SignUpLite;
    return <SocialLoginButtons action={action} />;
  }

  private renderOrDivider() {
    return (
      <div className={styles.divider}>
        <SignUpDivider />
      </div>
    );
  }

  private renderEmail() {
    return (
      <Form.Item
        className={styles.emailContainer}
        name='email'
        label='Sign up with your email'
        rules={[
          {
            type: 'email'
          },
          {
            required: true,
            max: 150
          }
        ]}
      >
        <Input
          autoComplete='off'
          className={styles.email}
          placeholder='Email'
        />
      </Form.Item>
    );
  }

  private renderRecaptchaV2() {
    if (!this.props.showReCaptchaV2) {
      return null;
    }

    return (
      <div className={styles.recaptchav2}>
        <ReCAPTCHA
          sitekey={RECAPTCHA_FALLBACK_SITE_KEY}
          onChange={this.handleRecaptchaV2Change}
        />
      </div>
    );
  }

  private renderNextButton() {
    return <Form.Item>
      <PrimaryButton
        loading={this.props.loading}
        htmlType='submit'
        className={styles.nextButton}>
        Next
      </PrimaryButton>
    </Form.Item>;
  }

  private renderAlreadyAnUser() {
    return <div className={styles.linkLogin}>
      Already have an account? <Link to="/login">Log in here</Link>
    </div>;
  }

  render(): JSX.Element {
    return (
      <Form
        autoComplete='off'
        layout='vertical'
        onFinish={this.handleSubmit}
        ref={this.formRef}
        requiredMark={false}
        validateTrigger={false}
        validateMessages={validateMessages}
        className={styles.form}
      >
        {this.renderTitle()}
        {this.renderSocialButtons()}
        {this.renderOrDivider()}
        {this.renderEmail()}
        {this.renderRecaptchaV2()}
        {this.renderNextButton()}
        {this.renderAlreadyAnUser()}
      </Form>
    );
  }
}

const mapDispatchToProps = {
  signUpByEmail,
  userLogout,
  showErrorMessage
};

const extractUtmParamsFromUrl = (): IUtmParams => {
  return {
    utmTerm: getQueryParam('utm_term')?.toString(),
    utmCampaign: getQueryParam('utm_campaign')?.toString(),
    utmSource: getQueryParam('utm_source')?.toString(),
    utmMedium: getQueryParam('utm_medium')?.toString()
  };
};

const mapStateToProps = (state: RootState): IMapStateToPros => {
  const { loading, showReCaptchaV2 } = selectSignUpByEmail(state);
  const utmParams = extractUtmParamsFromUrl();
  return {
    loading,
    showReCaptchaV2,
    utmParams
  };
};

const SignUpByEmailWithRecaptcha = withGoogleReCaptcha(SignUpByEmailComponent);
const SignUpByEmailWithRecaptchaAndSignUp = withSignUp(SignUpByEmailWithRecaptcha);

export const SignUpByEmail = connect(mapStateToProps, mapDispatchToProps)(SignUpByEmailWithRecaptchaAndSignUp);
